import React, { useState, useEffect } from "react";
import { Create } from "@refinedev/antd";
import { useCustomMutation, useOne } from "@refinedev/core";
import { message, Form, Card, Space, Button, Typography } from "antd";
import {
  CREATE_RECEIVING_MUTATION,
  SUBMIT_RECEIVING_MUTATION,
} from "./queries";
import { useLocation } from "react-router-dom";
import { PURCHASE_ORDER_BY_ID_QUERY } from "../purchase-order/queries";
import { PurchaseOrder } from "../purchase-order/interface";
import { ReceivingForm } from "./components/ReceivingForm";

export const CreateReceiving: React.FC = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromPOId = queryParams.get("fromPO");

  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [isLinkingMode, setIsLinkingMode] = useState<boolean>(!!fromPOId);

  const { data: poData, isLoading: poLoading } = useOne<PurchaseOrder>({
    resource: "purchaseOrders",
    id: fromPOId || "",
    queryOptions: {
      enabled: !!fromPOId,
    },
    meta: {
      gqlQuery: PURCHASE_ORDER_BY_ID_QUERY,
    },
  });

  useEffect(() => {
    if (poData?.data) {
      setSelectedPurchaseOrder(poData.data);
    }
  }, [poData]);

  // Create draft
  const { mutate: createMutate, isLoading: creating } = useCustomMutation();
  // Submit receiving (create + submit path via receivingData)
  const { mutate: submitMutate, isLoading: submitting } = useCustomMutation();

  const onSaveDraft = async (values: any) => {
    if (selectedPurchaseOrder) {
      createMutate(
        {
          url: "",
          method: "post",
          values: {
            ...values,
            poId: selectedPurchaseOrder.id,
            poNo: selectedPurchaseOrder.orderNo,
          },
          meta: { gqlMutation: CREATE_RECEIVING_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Receiving created and saved as draft");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to create receiving and save as draft");
          },
        },
      );
    } else {
      createMutate(
        {
          url: "",
          method: "post",
          values: values,
          meta: { gqlMutation: CREATE_RECEIVING_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Receiving saved as draft");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to save draft");
          },
        },
      );
    }
  };

  const onSubmit = async (values: any) => {
    if (selectedPurchaseOrder) {
      submitMutate(
        {
          url: "",
          method: "post",
          values: {
            receivingData: {
              ...values,
              poId: selectedPurchaseOrder.id,
              poNo: selectedPurchaseOrder.orderNo,
            },
          },
          meta: { gqlMutation: SUBMIT_RECEIVING_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Receiving created and submitted");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to create receiving and submit");
          },
        },
      );
    } else {
      submitMutate(
        {
          url: "",
          method: "post",
          values: {
            receivingData: values,
          },
          meta: { gqlMutation: SUBMIT_RECEIVING_MUTATION },
        },
        {
          onSuccess: () => {
            message.success("Receiving submitted");
          },
          onError: (e) => {
            console.error(e);
            message.error("Failed to submit receiving");
          },
        },
      );
    }
  };

  return (
    <Create
      title="Create Receiving"
      saveButtonProps={{ loading: creating || submitting, style: { display: "none" } }}
      footerButtons={() => null}
    >
      {!isLinkingMode && (
        <Card title="Select Purchase Order">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text>
              Select a purchase order to create a receiving entry, or create a standalone receiving.
            </Typography.Text>
            <Space>
              <Button
                type="primary"
                onClick={() => setIsLinkingMode(true)}
              >
                Create Receiving
              </Button>
              {fromPOId && (
                <Button
                  onClick={() => {
                    setSelectedPurchaseOrder(poData?.data || null);
                    setIsLinkingMode(true);
                  }}
                >
                  Use Selected PO
                </Button>
              )}
            </Space>
          </Space>
        </Card>
      )}

      {isLinkingMode && (
        <ReceivingForm
          form={form}
          onSaveDraft={onSaveDraft}
          onSubmit={onSubmit}
          purchaseOrderId={selectedPurchaseOrder?.id}
          mode="create"
        />
      )}
    </Create>
  );
};